/**
 * Pull a creator handle and platform out of whatever a spreadsheet cell
 * holds: a profile link with tracking junk, a bare @handle, a link missing
 * its https://, or a form answer with a stray space after the @.
 *
 * The username is then checked with the same rules the backend applies
 * (`normalizeHandle` in outreach-copy), so a row that passes here will not
 * bounce off the server for shape reasons.
 */
import type { InviteChannel } from '@/api/types';
import { channelLabel, normalizeHandle } from '@/lib/outreach-copy';

export type ExtractedHandle =
  | { ok: true; channel: InviteChannel; username: string; fromUrl: boolean }
  | { ok: false; channel: InviteChannel | null; reason: string };

const HOSTS: Array<{ re: RegExp; channel: InviteChannel }> = [
  { re: /(^|\.)tiktok\.com$/, channel: 'TIKTOK' },
  { re: /(^|\.)instagram\.com$|(^|\.)instagr\.am$/, channel: 'INSTAGRAM' },
  { re: /(^|\.)(x|twitter)\.com$/, channel: 'TWITTER' },
  { re: /(^|\.)youtube\.com$|(^|\.)youtu\.be$/, channel: 'YOUTUBE' },
  { re: /(^|\.)discord(app)?\.(com|gg)$/, channel: 'DISCORD' },
];

/** Path segments that are app pages, not usernames. */
const RESERVED: Partial<Record<InviteChannel, Set<string>>> = {
  INSTAGRAM: new Set(['p', 'reel', 'reels', 'tv', 'explore', 'accounts', 'direct', 'about', 'legal', 'stories']),
  TWITTER: new Set(['i', 'home', 'intent', 'search', 'hashtag', 'share', 'explore', 'notifications', 'messages', 'settings']),
  YOUTUBE: new Set(['watch', 'shorts', 'playlist', 'results', 'feed', 'embed', 'live']),
  TIKTOK: new Set(['t', 'discover', 'tag', 'music', 'foryou', 'following', 'live', 'search']),
};

const INVISIBLE = /[​-‍⁠﻿]/g;

const HOSTLIKE = /^(?:[a-z0-9-]+\.)+(?:com|am|be|gg|net|org|io|co|me|app)(?:[/?#]|$)/i;

/** Which platform a link points at, or null for any other site. */
export function channelForHost(host: string): InviteChannel | null {
  const h = host.toLowerCase().replace(/\.$/, '');
  return HOSTS.find((x) => x.re.test(h))?.channel ?? null;
}

function invalid(reason: string, channel: InviteChannel | null = null): ExtractedHandle {
  return { ok: false, channel, reason };
}

function finish(channel: InviteChannel, candidate: string, fromUrl: boolean): ExtractedHandle {
  const parsed = normalizeHandle(channel, candidate);
  if (!parsed) return invalid(`Not a valid ${channelLabel(channel)} handle`, channel);
  return { ok: true, channel, username: parsed.username, fromUrl };
}

function fromUrl(raw: string): ExtractedHandle {
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return invalid('Not a profile link');
  }
  const channel = channelForHost(url.hostname);
  if (!channel) return invalid(`Not a TikTok, Instagram, YouTube or X link (${url.hostname.replace(/^www\./, '')})`);
  const host = url.hostname.toLowerCase();
  // decodeURIComponent so "@%20name" (a space the browser encoded) collapses too.
  let segments: string[];
  try {
    segments = url.pathname.split('/').map((s) => decodeURIComponent(s).replace(/\s+/g, '')).filter(Boolean);
  } catch {
    segments = url.pathname.split('/').filter(Boolean);
  }
  const first = segments[0] ?? '';
  const reserved = RESERVED[channel] ?? new Set<string>();

  switch (channel) {
    case 'TIKTOK': {
      if (/^(vm|vt)\./.test(host) || first.toLowerCase() === 't') {
        return invalid('Short TikTok link. Open it and copy the profile link instead', channel);
      }
      if (/^@MS4wLjABAAAA/.test(first)) {
        return invalid('TikTok link with an internal id, not a username. Open it and copy the @handle', channel);
      }
      if (!first.startsWith('@')) {
        return invalid(reserved.has(first.toLowerCase()) || !first ? 'Not a TikTok profile link' : 'TikTok profile links look like tiktok.com/@name', channel);
      }
      return finish(channel, first, true);
    }
    case 'INSTAGRAM': {
      const lower = first.toLowerCase();
      if (lower === 'stories' && segments[1]) return finish(channel, segments[1], true);
      if (!first || reserved.has(lower)) return invalid('Post or page link, not an Instagram profile', channel);
      return finish(channel, first, true);
    }
    case 'TWITTER': {
      if (!first || reserved.has(first.toLowerCase())) return invalid('Not an X profile link', channel);
      return finish(channel, first, true);
    }
    case 'YOUTUBE': {
      if (host.endsWith('youtu.be')) return invalid('Video link, not a YouTube channel', channel);
      const lower = first.toLowerCase();
      if (first.startsWith('@')) return finish(channel, first, true);
      if ((lower === 'c' || lower === 'user' || lower === 'channel') && segments[1]) return finish(channel, segments[1], true);
      if (!first || reserved.has(lower)) return invalid('Video link, not a YouTube channel', channel);
      return finish(channel, first, true);
    }
    case 'DISCORD':
      return invalid('Discord invite link. Use their Discord username instead', channel);
    default:
      return invalid('Not a profile link');
  }
}

/**
 * Read one cell. `fallback` is the platform for plain handles (no link);
 * without one, plain handles are invalid. A link always decides its own
 * platform.
 */
export function extractHandle(cell: string | null | undefined, fallback: InviteChannel | null): ExtractedHandle {
  const raw = String(cell ?? '')
    .replace(INVISIBLE, '')
    .replace(/ /g, ' ')
    .trim()
    // "@ name" and "/@ name": a space typed after the @.
    .replace(/@\s+/g, '@');
  if (!raw) return invalid('Empty');

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) || /^www\./i.test(raw) || HOSTLIKE.test(raw)) {
    // A link with spaces in it ("tiktok.com/@ name") was fixed above; any
    // other whitespace is junk after the link (a second answer, a note).
    return fromUrl(raw.split(/\s+/)[0]!);
  }

  if (/\s/.test(raw)) return invalid('Has spaces. Expected one handle or profile link');
  if (!fallback) return invalid('Plain handle. Pick its platform in Columns');
  return finish(fallback, raw, false);
}

/** "@name" with its platform, for display. */
export function displayHandle(username: string): string {
  return `@${username}`;
}

/** The dedupe key for one creator. */
export function creatorKey(channel: InviteChannel, username: string): string {
  return `${channel}:${username.toLowerCase()}`;
}
